import { useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import AgingBadge from '../shared/AgingBadge';
import VendorTag from '../shared/VendorTag';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatDate } from '../../utils/formatters';

export default function UnreturnedCores({ data }) {
  const items = data?.items || [];

  const totalCoreValue = data?.total_core_value_usd || items.reduce((s, c) => s + (c.core_charge_usd || 0), 0);
  const avgDays = items.length > 0
    ? Math.round(items.reduce((s, c) => s + (c.days_since_received || 0), 0) / items.length)
    : 0;

  const columns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'ro_number',
      label: 'RO / Customer',
      render: (row) => (
        <div>
          <div className="font-semibold text-sm">#{row.ro_number}</div>
          <div className="text-xs text-gray-400">{row.customer_name} &middot; {row.vehicle_description}</div>
        </div>
      ),
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <VendorTag name={row.vendor_name} />,
    },
    {
      key: 'received_date',
      label: 'Received',
      render: (row) => <span className="text-xs">{formatDate(row.received_date)}</span>,
    },
    {
      key: 'core_charge_usd',
      label: 'Core Charge',
      align: 'right',
      render: (row) => <span className="font-extrabold text-red-700">{formatMoney(row.core_charge_usd)}</span>,
    },
    {
      key: 'days_since_received',
      label: 'Age',
      align: 'right',
      render: (row) => <AgingBadge days={row.days_since_received || 0} />,
    },
  ], []);

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Unreturned Cores"
          value={items.length.toString()}
          accent="#E65100"
          icon={'\uD83D\uDD27'}
        />
        <MetricCard
          label="Core Value at Risk"
          value={formatMoney(totalCoreValue)}
          sub="Refundable if returned"
          accent="#C62828"
          icon={'\uD83D\uDCB0'}
        />
        <MetricCard
          label="Avg Days Held"
          value={avgDays.toString()}
          sub="Since part received"
          icon={'\u23F1'}
        />
      </div>

      <div className="text-xs text-gray-500 mb-4 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
        Parts with core charges that have been received but the old core has not been returned to the vendor. Most vendors require core returns within 30-60 days.
      </div>

      {items.length > 0 ? (
        <DataTable columns={columns} data={items} />
      ) : (
        <EmptyState text="No unreturned cores found" />
      )}
    </div>
  );
}
